// VideoViewer.jsx — <video> з «підштовхуванням», подвійним тапом і свайпом

import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const fileSrc = (id) => `/drive.php?action=img&id=${encodeURIComponent(id)}`;

export default function VideoViewer({ item, onNext, onPrev }) {
    const videoRef = useRef(null);
    const touchStartX = useRef(null);
    const lastTapRef = useRef(0);
    const src = fileSrc(item.id);

    // «Підштовхування» відео, якщо потік «засинає»
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;

        let lastTime = 0;
        let lastUpdate = performance.now();
        const tick = () => {
            if (!v || v.paused) return;
            const now = performance.now();
            if (Math.abs(v.currentTime - lastTime) < 0.05) {
                if (now - lastUpdate > 1500) {
                    try {
                        v.currentTime = v.currentTime + 0.001;
                        lastUpdate = now;
                    } catch {
                        /* ignore */
                    }
                }
            } else {
                lastTime = v.currentTime;
                lastUpdate = now;
            }
        };
        const id = setInterval(tick, 500);
        return () => clearInterval(id);
    }, [item?.id]);

    const toggleFullscreen = () => {
        const v = videoRef.current;
        if (!v) return;

        const doc = document;

        if (!doc.fullscreenElement) {
            if (v.requestFullscreen) {
                v.requestFullscreen().catch(() => { });
            }
        } else if (doc.exitFullscreen) {
            doc.exitFullscreen().catch(() => { });
        }
    };

    // Подвійний клік для десктопа
    const handleDoubleClick = (e) => {
        e.stopPropagation();
        toggleFullscreen();
    };

    // Тач-обробники: подвійний тап + свайп ліворуч/праворуч
    const handleTouchStart = (e) => {
        if (!e.changedTouches || e.changedTouches.length === 0) return;
        touchStartX.current = e.changedTouches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        if (!e.changedTouches || e.changedTouches.length === 0) return;

        const touchX = e.changedTouches[0].clientX;
        const startX = touchStartX.current ?? touchX;
        const deltaX = touchX - startX;

        const now = Date.now();

        // Подвійний тап (~300ms)
        if (now - lastTapRef.current < 300) {
            lastTapRef.current = 0;
            toggleFullscreen();
            return;
        }
        lastTapRef.current = now;

        // Свайп (порог ~50px)
        const threshold = 50;
        if (Math.abs(deltaX) > threshold) {
            if (deltaX < 0 && typeof onNext === 'function') {
                onNext();
            } else if (deltaX > 0 && typeof onPrev === 'function') {
                onPrev();
            }
        }
    };

    return (
        <div
            className="relative overflow-hidden rounded-xl shadow-2xl bg-black"
            style={{ height: '78vh', width: '92vw' }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={handleDoubleClick}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
        >
            <video
                key={item.id}
                ref={videoRef}
                src={src}
                className="block w-full h-full object-contain bg-black"
                controls
                preload="metadata"
                playsInline
            />
        </div>
    );
}

VideoViewer.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string,
    }).isRequired,
    onNext: PropTypes.func,   // викликається при свайпі вліво
    onPrev: PropTypes.func,   // викликається при свайпі вправо
};
